import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useBranch } from '../../../layouts/BranchContext';

import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Modal,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Snackbar,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';

/* =========================================================
   ALERT
========================================================= */

const Alert = React.forwardRef(function Alert(
  props,
  ref
) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

/* =========================================================
   API
========================================================= */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem(
        'accessToken'
      );

    if (token) {
      config.headers =
        config.headers || {};

      config.headers.Authorization =
        `Bearer ${token}`;
    }

    return config;
  },
  (error) =>
    Promise.reject(error)
);

/* =========================================================
   STATUS MAPPING
========================================================= */

const statusMapping = {
  'On Hold': 'on hold',
  'In Process': 'in progress',
  Done: 'completed',
  'To Start': 'to start',
  'In Progress': 'in progress',
  Completed: 'completed',
};

/* =========================================================
   HELPER FUNCTIONS
========================================================= */

const getSessionUser = () => {
  const possibleKeys = [
    'user',
    'currentUser',
    'userData',
    'loggedInUser',
    'userDetails',
  ];

  for (const key of possibleKeys) {
    try {
      const value =
        sessionStorage.getItem(key);

      if (value) {
        const parsed =
          JSON.parse(value);

        if (
          parsed &&
          typeof parsed === 'object'
        ) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn(
        `Unable to parse sessionStorage key: ${key}`
      );
    }
  }

  return {};
};

const getSessionOrganizationId =
  () => {
    const user =
      getSessionUser();

    return (
      user.organization_id ??
      user.organizationId ??
      user.organization?.id ??
      user.organization ??
      sessionStorage.getItem(
        'organization_id'
      ) ??
      localStorage.getItem(
        'organization_id'
      ) ??
      ''
    );
  };

const getSessionBranchId = () => {
  const user =
    getSessionUser();

  return (
    user.branch_id ??
    user.branchId ??
    user.branch?.id ??
    user.branch ??
    sessionStorage.getItem(
      'branch_id'
    ) ??
    localStorage.getItem(
      'branch_id'
    ) ??
    ''
  );
};

const getId = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '';
  }

  if (
    typeof value === 'object'
  ) {
    return value.id ?? '';
  }

  return value;
};

/* =========================================================
   COURSE ORGANIZATION
========================================================= */

const getOrganizationIdFromCourse =
  (course) => {
    if (!course) return '';

    return getId(
      course.organization_id ??
        course.organizationId ??
        course.organization?.id ??
        course.organization
    );
  };

/* =========================================================
   COURSE BRANCH
========================================================= */

const getBranchIdFromCourse =
  (course) => {
    if (!course) return '';

    return getId(
      course.branch_id ??
        course.branchId ??
        course.branch?.id ??
        course.branch
    );
  };

/* =========================================================
   EMPLOYEE NAME
========================================================= */

const getEmployeeName = (
  employee
) => {
  if (!employee) return '';

  if (employee.name) {
    return employee.name;
  }

  if (employee.full_name) {
    return employee.full_name;
  }

  const firstName =
    employee.first_name ||
    employee.firstName ||
    '';

  const lastName =
    employee.last_name ||
    employee.lastName ||
    '';

  const fullName =
    `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  return `Employee ${employee.id}`;
};

/* =========================================================
   EMPLOYEE DESIGNATION
========================================================= */

const getEmployeeDesignation = (
  employee
) => {
  if (!employee) return '';

  const designation =
    employee.designation;

  if (
    typeof designation ===
    'string'
  ) {
    return designation;
  }

  if (
    designation &&
    typeof designation ===
      'object'
  ) {
    return (
      designation.name ||
      designation.designation_name ||
      designation.title ||
      ''
    );
  }

  return (
    employee.designation_name ||
    employee.designationName ||
    employee.designation_title ||
    employee.role_name ||
    employee.role ||
    ''
  );
};

/* =========================================================
   CHECK TRAINER
========================================================= */

const isTrainerEmployee = (
  employee
) => {
  const designation =
    getEmployeeDesignation(
      employee
    )
      .toString()
      .trim()
      .toLowerCase();

  const role = (
    employee?.role_name ||
    employee?.role ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();

  const department = (
    employee?.department_name ||
    employee?.department ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();

  return (
    designation.includes(
      'trainer'
    ) ||
    designation.includes(
      'train'
    ) ||
    role.includes(
      'trainer'
    ) ||
    role.includes(
      'train'
    ) ||
    department.includes(
      'trainer'
    ) ||
    department.includes(
      'train'
    )
  );
};

/* =========================================================
   EXTRACT API LIST
========================================================= */

const extractList = (
  responseData
) => {
  if (
    Array.isArray(
      responseData
    )
  ) {
    return responseData;
  }

  if (
    Array.isArray(
      responseData?.results
    )
  ) {
    return responseData.results;
  }

  if (
    Array.isArray(
      responseData?.data
    )
  ) {
    return responseData.data;
  }

  if (
    Array.isArray(
      responseData?.employees
    )
  ) {
    return responseData.employees;
  }

  if (
    Array.isArray(
      responseData?.staff
    )
  ) {
    return responseData.staff;
  }

  return [];
};

/* =========================================================
   COMPONENT
========================================================= */

const AddBatch = () => {
  const theme = useTheme();

  const navigate =
    useNavigate();

  /* =========================================================
     SESSION VALUES
  ========================================================= */

  const sessionOrganizationId =
    getSessionOrganizationId();

  const sessionBranchId =
    getSessionBranchId();

  /* =========================================================
     UI STATES
  ========================================================= */
const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();
  const [
    showSuccessModal,
    setShowSuccessModal,
  ] = useState(false);

  const [
    openSnackbar,
    setOpenSnackbar,
  ] = useState(false);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState('');

  const [
    snackbarSeverity,
    setSnackbarSeverity,
  ] = useState('success');

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    loadingData,
    setLoadingData,
  ] = useState(true);

  const [
    loadingTrainers,
    setLoadingTrainers,
  ] = useState(false);

  /* =========================================================
     TIME PICKER
  ========================================================= */

  const [
    openTimePicker,
    setOpenTimePicker,
  ] = useState(false);

  const [
    selectedStartTime,
    setSelectedStartTime,
  ] = useState('09:00');

  const [
    selectedEndTime,
    setSelectedEndTime,
  ] = useState('11:00');

  const [
    selectedAmPmStart,
    setSelectedAmPmStart,
  ] = useState('AM');

  const [
    selectedAmPmEnd,
    setSelectedAmPmEnd,
  ] = useState('AM');

  /* =========================================================
     DATA STATES
  ========================================================= */

  const [
    courses,
    setCourses,
  ] = useState([]);

  const [
    filteredCourses,
    setFilteredCourses,
  ] = useState([]);

  const [
    trainers,
    setTrainers,
  ] = useState([]);

  const [
    organizations,
    setOrganizations,
  ] = useState([]);

  const [
    branches,
    setBranches,
  ] = useState([]);

  const [
    allBranches,
    setAllBranches,
  ] = useState([]);

  const [
    selectedCourseDuration,
    setSelectedCourseDuration,
  ] = useState(null);

  /* =========================================================
     STATUS
  ========================================================= */

  const batchStatuses = [
    {
      id: 1,
      status_name: 'To Start',
    },
    {
      id: 2,
      status_name: 'On Hold',
    },
    {
      id: 3,
      status_name: 'Completed',
    },
  ];

  /* =========================================================
     FORM
  ========================================================= */

 const [formData, setFormData] = useState({
  batch_name: '',
  course: '',
  trainer: '',
  start_date: new Date().toISOString().split('T')[0],
  completion_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  batch_time: '',
  batch_size: '',
  batch_status: '',
  remark: '',
  organization: sessionOrganizationId,
  branch: selectedBranchId, // Use global branch ID
});

  /* =========================================================
     TIME OPTIONS
  ========================================================= */

  const hours =
    Array.from(
      { length: 12 },
      (_, i) =>
        (i + 1)
          .toString()
          .padStart(2, '0')
    );

  const minutes =
    Array.from(
      { length: 60 },
      (_, i) =>
        i.toString().padStart(
          2,
          '0'
        )
    );

  const amPmOptions = [
    'AM',
    'PM',
  ];

  /* =========================================================
     AUTH
  ========================================================= */

  const isAuthenticated =
    () => {
      return !!localStorage.getItem(
        'accessToken'
      );
    };

  /* =========================================================
     SNACKBAR
  ========================================================= */

  const showMessage = (
    message,
    severity = 'success'
  ) => {
    setSnackbarMessage(
      message
    );

    setSnackbarSeverity(
      severity
    );

    setOpenSnackbar(true);
  };

  /* =========================================================
     INITIAL DATA
  ========================================================= */

  useEffect(() => {
    if (
      !isAuthenticated()
    ) {
      navigate('/login');
      return;
    }

    fetchInitialData();
  }, [navigate]);

 const fetchInitialData = async () => {
  try {
    setLoadingData(true);
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const organizationId = getSessionOrganizationId();
    if (!organizationId) {
      showMessage('Organization information is missing. Please login again.', 'error');
      return;
    }

    console.log("====================================");
    console.log("INITIAL DATA");
    console.log("SESSION ORGANIZATION:", organizationId);
    console.log("SESSION BRANCH:", getSessionBranchId());
    console.log("====================================");

    const [coursesRes, organizationsRes] = await Promise.all([
      api.get('/api/course/courses/'),
      api.get('/api/master/organizations/'),
    ]);

    const coursesData = extractList(coursesRes.data);
    const organizationsData = extractList(organizationsRes.data);

    console.log("ALL COURSES:", coursesData);
    console.log("ALL ORGANIZATIONS:", organizationsData);

    const userOrganizationId = Number(organizationId);

    // Organization
    const currentOrganization = organizationsData.find(
      (organization) => Number(organization.id) === userOrganizationId
    );
    const currentOrganizationList = currentOrganization ? [currentOrganization] : [];

    setCourses(coursesData);
    setOrganizations(currentOrganizationList);

    // Keep organization fixed to logged-in user.
    setFormData((prev) => ({
      ...prev,
      organization: organizationId,
      branch: prev.branch || getSessionBranchId() || '',
    }));
  } catch (error) {
    console.error("INITIAL DATA ERROR:", error);
    if (error.response?.status === 401) {
      showMessage('Your session has expired. Please login again.', 'error');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      return;
    }
    let message = 'Failed to load initial data. Please refresh the page.';
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        message = error.response.data;
      } else if (error.response.data.detail) {
        message = error.response.data.detail;
      } else if (error.response.data.message) {
        message = error.response.data.message;
      }
    }
    showMessage(message, 'error');
  } finally {
    setLoadingData(false);
  }
};



 useEffect(() => {
  const organizationId = Number(formData.organization);
  const branchId = Number(selectedBranchId); // Use global branch ID

  if (!organizationId || !branchId || !courses.length) {
    setFilteredCourses([]);
    if (formData.course) {
      setFormData((prev) => ({
        ...prev,
        course: '',
        trainer: '',
      }));
      setSelectedCourseDuration(null);
    }
    return;
  }

  const filtered = courses.filter((course) => {
    if (course.is_active === false) {
      return false;
    }
    const courseOrganizationId = Number(getOrganizationIdFromCourse(course));
    const courseBranchIdRaw = getBranchIdFromCourse(course);
    const courseBranchId = courseBranchIdRaw === '' ? '' : Number(courseBranchIdRaw);

    if (courseOrganizationId !== organizationId) {
      return false;
    }
    if (courseBranchId !== '' && courseBranchId !== branchId) {
      return false;
    }
    return true;
  });

  console.log("====================================");
  console.log("COURSE FILTER");
  console.log("ORGANIZATION:", organizationId);
  console.log("BRANCH:", branchId);
  console.log("FILTERED COURSES:", filtered);

  setFilteredCourses(filtered);

  if (formData.course && !filtered.some((course) => Number(course.id) === Number(formData.course))) {
    setFormData((prev) => ({
      ...prev,
      course: '',
      trainer: '',
    }));
    setSelectedCourseDuration(null);
  }
}, [courses, formData.organization, selectedBranchId, formData.course]);

  /* =========================================================
     CALCULATE COMPLETION DATE
  ========================================================= */

  const calculateCompletionDate =
    (
      startDate,
      duration
    ) => {
      if (
        !startDate ||
        !duration
      ) {
        return '';
      }

      const durationString =
        duration
          .toString()
          .toLowerCase()
          .trim();

      const match =
        durationString.match(
          /(\d+(?:\.\d+)?)/
        );

      if (!match) {
        return '';
      }

      const durationValue =
        parseFloat(
          match[1]
        );

      let durationUnit =
        'months';

      if (
        durationString.includes(
          'year'
        )
      ) {
        durationUnit =
          'years';
      } else if (
        durationString.includes(
          'day'
        )
      ) {
        durationUnit =
          'days';
      } else if (
        durationString.includes(
          'week'
        )
      ) {
        durationUnit =
          'weeks';
      } else if (
        durationString.includes(
          'month'
        ) ||
        durationString.match(
          /^\d+(\.\d+)?$/
        )
      ) {
        durationUnit =
          'months';
      }

      const date =
        new Date(
          startDate
        );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return '';
      }

      if (
        durationUnit ===
        'years'
      ) {
        date.setMonth(
          date.getMonth() +
            durationValue *
              12
        );
      } else if (
        durationUnit ===
        'months'
      ) {
        date.setMonth(
          date.getMonth() +
            durationValue
        );
      } else if (
        durationUnit ===
        'weeks'
      ) {
        date.setDate(
          date.getDate() +
            durationValue *
              7
        );
      } else if (
        durationUnit ===
        'days'
      ) {
        date.setDate(
          date.getDate() +
            durationValue
        );
      }

      return date
        .toISOString()
        .split('T')[0];
    };

  /* =========================================================
     COURSE DURATION
  ========================================================= */

  useEffect(() => {
    if (
      !formData.course ||
      !formData.start_date
    ) {
      setSelectedCourseDuration(
        null
      );

      return;
    }

    const selectedCourse =
      filteredCourses.find(
        (course) =>
          Number(
            course.id
          ) ===
          Number(
            formData.course
          )
      );

    if (
      !selectedCourse
    ) {
      setSelectedCourseDuration(
        null
      );

      return;
    }

    const duration =
      selectedCourse.course_duration ??
      selectedCourse.duration ??
      selectedCourse.courseDuration;

    if (!duration) {
      setSelectedCourseDuration(
        null
      );

      return;
    }

    setSelectedCourseDuration(
      duration
    );

    const completionDate =
      calculateCompletionDate(
        formData.start_date,
        duration
      );

    if (
      completionDate &&
      completionDate !==
        formData.completion_date
    ) {
      setFormData(
        (prev) => ({
          ...prev,
          completion_date:
            completionDate,
        })
      );
    }
  }, [
    formData.course,
    formData.start_date,
    filteredCourses,
  ]);

  /* =========================================================
     LOAD TRAINERS
     ORGANIZATION + BRANCH ONLY
  ========================================================= */

 const fetchTrainers = useCallback(
  async (organizationId, branchId = selectedBranchId) => { // Use global branch ID
    if (!organizationId || !branchId) {
      console.warn('Trainer API skipped:', { organizationId, branchId });
      setTrainers([]);
      return;
    }
    try {
      setLoadingTrainers(true);
      console.log("====================================");
      console.log("FETCHING TRAINERS");
      console.log("ORGANIZATION:", organizationId);
      console.log("BRANCH:", branchId);

      const response = await api.get('/api/staff/employees/filter/', {
        params: {
          organization_id: Number(organizationId),
          branch_id: Number(branchId),
        },
      });

      console.log("TRAINER API STATUS:", response.status);
      console.log("TRAINER API RESPONSE:", response.data);

      const employees = extractList(response.data);
      console.log("EMPLOYEES FROM API:", employees);

      const organizationAndBranchEmployees = employees.filter((employee) => {
        const employeeOrganizationId = getId(
          employee.organization_id ?? employee.organizationId ?? employee.organization
        );
        const employeeBranchId = getId(
          employee.branch_id ?? employee.branchId ?? employee.branch
        );
        const hasOrganization = employeeOrganizationId !== '';
        const hasBranch = employeeBranchId !== '';
        const organizationMatches = !hasOrganization || Number(employeeOrganizationId) === Number(organizationId);
        const branchMatches = !hasBranch || Number(employeeBranchId) === Number(branchId);
        return organizationMatches && branchMatches;
      });

      console.log("EMPLOYEES FOR SELECTED ORGANIZATION + BRANCH:", organizationAndBranchEmployees);
      const trainerEmployees = organizationAndBranchEmployees.filter(isTrainerEmployee);
      console.log("TRAINERS AFTER FILTER:", trainerEmployees);
      setTrainers(trainerEmployees);
      if (trainerEmployees.length === 0) {
        console.warn('No trainers found for selected organization + branch.');
      }
    } catch (error) {
      console.error("TRAINER API ERROR:", error);
      console.error("TRAINER API RESPONSE ERROR:", error.response?.data);
      setTrainers([]);
      if (error.response?.status === 401) {
        showMessage('Your session has expired. Please login again.', 'error');
        return;
      }
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        console.error("400 TRAINER ERROR:", errorData);
        showMessage(
          errorData?.organization_id || errorData?.branch_id || errorData?.detail || 'Organization and Branch are required to load trainers.',
          'error'
        );
      }
    } finally {
      setLoadingTrainers(false);
    }
  },
  [selectedBranchId]
);

  /* =========================================================
     HANDLE CHANGE
  ========================================================= */

  const handleChange = (e) => {
  const { name, value } = e.target;

  // Ignore branch changes (handled globally)
  if (name === 'branch') {
    return;
  }

  // Organization
  if (name === 'organization') {
    const fixedOrganizationId = getSessionOrganizationId();
    setFormData((prev) => ({
      ...prev,
      organization: fixedOrganizationId,
    }));
    return;
  }

  // Course
  if (name === 'course') {
    const selectedCourse = filteredCourses.find(
      (course) => Number(course.id) === Number(value)
    );
    if (!selectedCourse) {
      showMessage('Invalid course selected for this branch.', 'error');
      return;
    }
    console.log("====================================");
    console.log("COURSE SELECTED:", selectedCourse);
    console.log("FIXED ORGANIZATION:", formData.organization);
    console.log("FIXED BRANCH:", selectedBranchId); // Use global branch ID

    setFormData((prev) => ({
      ...prev,
      course: value,
      trainer: '',
    }));

    if (formData.organization && selectedBranchId) { // Use global branch ID
      fetchTrainers(formData.organization, selectedBranchId);
    }
    return;
  }

  // Start Date
  if (name === 'start_date') {
    let completionDate = formData.completion_date;
    if (formData.course) {
      const selectedCourse = filteredCourses.find(
        (course) => Number(course.id) === Number(formData.course)
      );
      const duration = selectedCourse?.course_duration ?? selectedCourse?.duration;
      if (duration) {
        completionDate = calculateCompletionDate(value, duration);
      }
    }
    setFormData((prev) => ({
      ...prev,
      start_date: value,
      completion_date: completionDate,
    }));
    return;
  }

  // All other fields
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};

  /* =========================================================
     TIME PICKER
  ========================================================= */

  const handleOpenTimePicker =
    () => {
      if (
        formData.batch_time
      ) {
        const parts =
          formData.batch_time.split(
            ' - '
          );

        if (
          parts.length === 2
        ) {
          const start =
            parts[0].split(
              ' '
            );

          const end =
            parts[1].split(
              ' '
            );

          if (
            start.length === 2 &&
            end.length === 2
          ) {
            setSelectedStartTime(
              start[0]
            );

            setSelectedAmPmStart(
              start[1]
            );

            setSelectedEndTime(
              end[0]
            );

            setSelectedAmPmEnd(
              end[1]
            );
          }
        }
      }

      setOpenTimePicker(
        true
      );
    };

  const handleApplyTime =
    () => {
      const timeString =
        `${selectedStartTime} ${selectedAmPmStart} - ${selectedEndTime} ${selectedAmPmEnd}`;

      setFormData(
        (prev) => ({
          ...prev,

          batch_time:
            timeString,
        })
      );

      setOpenTimePicker(
        false
      );
    };

  const handleCloseTimePicker =
    () => {
      setOpenTimePicker(
        false
      );
    };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
  const requiredFields = [
    'batch_name',
    'course',
    'trainer',
    'start_date',
    'completion_date',
    'batch_time',
    'batch_size',
    'batch_status',
    'organization',
    // Remove branch validation (handled globally)
    // 'branch',
  ];

  for (const field of requiredFields) {
    if (!formData[field]) {
      return `Please fill in ${field.replace('_', ' ')}`;
    }
  }

  // Organization security
  const loggedInOrganizationId = Number(getSessionOrganizationId());
  if (Number(formData.organization) !== loggedInOrganizationId) {
    return 'Invalid organization selected.';
  }

  // Remove branch security check (handled globally)
  // const selectedBranch = branches.find(
  //   (branch) => Number(branch.id) === Number(formData.branch)
  // );
  // if (!selectedBranch) {
  //   return 'Invalid branch selected.';
  // }
  // const branchOrganizationId = getId(
  //   selectedBranch.organization_id ?? selectedBranch.organizationId ?? selectedBranch.organization
  // );
  // if (branchOrganizationId && Number(branchOrganizationId) !== loggedInOrganizationId) {
  //   return 'Selected branch does not belong to your organization.';
  // }

  // Dates
  const startDate = new Date(formData.start_date);
  const completionDate = new Date(formData.completion_date);
  if (completionDate < startDate) {
    return 'Completion date cannot be before start date';
  }

  // Course security
  const selectedCourse = filteredCourses.find(
    (course) => Number(course.id) === Number(formData.course)
  );
  if (!selectedCourse) {
    return 'Invalid course selected for your organization and branch.';
  }
  const courseOrganizationId = Number(getOrganizationIdFromCourse(selectedCourse));
  if (courseOrganizationId !== loggedInOrganizationId) {
    return 'Selected course does not belong to your organization.';
  }
  const courseBranchIdRaw = getBranchIdFromCourse(selectedCourse);
  if (courseBranchIdRaw !== '' && Number(courseBranchIdRaw) !== Number(selectedBranchId)) { // Use global branch ID
    return 'Selected course does not belong to the selected branch.';
  }

  // Course duration
  const duration = selectedCourse?.course_duration ?? selectedCourse?.duration;
  if (duration) {
    const expectedDate = calculateCompletionDate(formData.start_date, duration);
    if (expectedDate && expectedDate !== formData.completion_date) {
      return `Batch duration must match course duration (${duration}). Expected completion date: ${expectedDate}`;
    }
  }

  // Trainer
  const selectedTrainer = trainers.find(
    (trainer) => Number(trainer.id) === Number(formData.trainer)
  );
  if (!selectedTrainer) {
    return 'Invalid trainer selected for the selected organization and branch.';
  }

  // Batch size
  const batchSize = parseInt(formData.batch_size, 10);
  if (Number.isNaN(batchSize) || batchSize <= 0) {
    return 'Batch size must be a positive number';
  }

  return null;
};

  /* =========================================================
     SERVER STATUS
  ========================================================= */

  const getServerStatusValue =
    (displayValue) => {
      if (
        statusMapping[
          displayValue
        ]
      ) {
        return statusMapping[
          displayValue
        ];
      }

      return displayValue
        .toString()
        .toLowerCase();
    };

  /* =========================================================
     SAVE
  ========================================================= */

  const handleSave = async (e) => {
  e.preventDefault();
  if (!isAuthenticated()) {
    showMessage('Please login to continue', 'error');
    navigate('/login');
    return;
  }

  const validationError = validateForm();
  if (validationError) {
    showMessage(validationError, 'error');
    return;
  }

  setLoading(true);
  try {
    const serverStatusValue = getServerStatusValue(formData.batch_status);
    const organizationId = Number(getSessionOrganizationId());
    const branchId = Number(selectedBranchId); // Use global branch ID

    const payload = {
      batch_name: formData.batch_name,
      course: Number(formData.course),
      trainer: Number(formData.trainer),
      start_date: formData.start_date,
      completion_date: formData.completion_date,
      batch_time: formData.batch_time,
      batch_size: Number(formData.batch_size),
      batch_status: serverStatusValue,
      remark: formData.remark || '',
      organization: organizationId,
      branch: branchId, // Use global branch ID
    };

    console.log("====================================");
    console.log("CREATE BATCH PAYLOAD:", payload);
    const response = await api.post('/api/course/batches/', payload);
    console.log("CREATE BATCH RESPONSE:", response.data);
    if (response.status === 200 || response.status === 201) {
      setShowSuccessModal(true);
    } else {
      throw new Error('Failed to save batch');
    }
  } catch (error) {
    console.error("SAVE BATCH ERROR:", error);
    console.error("SAVE BATCH RESPONSE:", error.response?.data);
    if (error.response?.status === 401) {
      showMessage('Your session has expired. Please login again.', 'error');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      return;
    }
    let errorMessage = 'Failed to save batch. Please try again.';
    const errorData = error.response?.data;
    if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (errorData?.detail) {
      errorMessage = errorData.detail;
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    } else if (errorData && typeof errorData === 'object') {
      const firstKey = Object.keys(errorData)[0];
      if (firstKey) {
        const value = errorData[firstKey];
        errorMessage = `${firstKey}: ${Array.isArray(value) ? value.join(', ') : value}`;
      }
    }
    showMessage(errorMessage, 'error');
  } finally {
    setLoading(false);
  }
};

  /* =========================================================
     CANCEL
  ========================================================= */

  const handleCancel =
    () => {
      navigate(-1);
    };

  /* =========================================================
     RESET
  ========================================================= */

 const handleReset = () => {
  const today = new Date().toISOString().split('T')[0];
  const defaultCompletionDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  setFormData({
    batch_name: '',
    course: '',
    trainer: '',
    start_date: today,
    completion_date: defaultCompletionDate,
    batch_time: '',
    batch_size: '',
    batch_status: '',
    remark: '',
    organization: getSessionOrganizationId(),
    branch: selectedBranchId, // Use global branch ID
  });

  setSelectedCourseDuration(null);
  setSelectedStartTime('09:00');
  setSelectedEndTime('11:00');
  setSelectedAmPmStart('AM');
  setSelectedAmPmEnd('AM');
  setTrainers([]);
};

  /* =========================================================
     DEBUG
  ========================================================= */

  useEffect(() => {
    console.log(
      '========== ADD BATCH STATE =========='
    );

    console.log(
      'ORGANIZATION:',
      formData.organization
    );

    console.log(
      'BRANCH:',
      formData.branch
    );

    console.log(
      'COURSE:',
      formData.course
    );

    console.log(
      'TRAINER:',
      formData.trainer
    );

    console.log(
      'ALL COURSES:',
      courses.length
    );

    console.log(
      'FILTERED COURSES:',
      filteredCourses.length
    );

    console.log(
      'TRAINERS:',
      trainers.length
    );

    console.log(
      'BRANCHES:',
      branches.length
    );
  }, [
    formData,
    courses,
    filteredCourses,
    trainers,
    branches,
  ]);

  /* =========================================================
     AUTH GUARD
  ========================================================= */

  if (
    !isAuthenticated()
  ) {
    return null;
  }

  /* =========================================================
     LOADING
  ========================================================= */

  if (
    loadingData
  ) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          my: 4,

          display:
            'flex',

          justifyContent:
            'center',

          alignItems:
            'center',

          minHeight:
            '400px',
        }}
      >
        <Box
          sx={{
            textAlign:
              'center',
          }}
        >
          <CircularProgress />

          <Typography
            sx={{
              mt: 2,
            }}
          >
            Loading data...
          </Typography>
        </Box>
      </Container>
    );
  }

  /* =========================================================
     COMMON FIELD STYLE
  ========================================================= */

  const fieldSx = {
    '& .MuiInputLabel-root': {
      color:
        theme.palette.text
          .primary,
    },

    '& .MuiInputLabel-root.Mui-focused':
      {
        color:
          theme.palette.primary
            .main,
      },

    '& .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.text
            .primary,
      },

    '&:hover .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.primary
            .main,
      },
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <Container
      maxWidth="lg"
      sx={{
        my: 0,
      }}
    >
      <Box
        sx={{
          p: 4,

          borderRadius: 2,

          backgroundColor:
            theme.palette
              .background
              .paper,
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display:
              'flex',

            justifyContent:
              'space-between',

            alignItems:
              'center',

            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight:
                'bold',

              color:
                theme.palette
                  .primary
                  .main,
            }}
          >
            Add New Batch
          </Typography>
        </Box>

        <form
          onSubmit={
            handleSave
          }
        >
          {/* =================================================
              ORGANIZATION + BRANCH
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              gap: 2,

              mb: 3,
            }}
          >
          
          </Box>

          {/* =================================================
              BATCH NAME + COURSE
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              gap: 2,

              mb: 3,
            }}
          >
            <TextField
              label="Batch Name"
              variant="outlined"
              fullWidth
              name="batch_name"
              value={
                formData.batch_name
              }
              onChange={
                handleChange
              }
              required
              disabled={
                loading
              }
              sx={
                fieldSx
              }
            />

            {/* =================================================
                COURSE
            ================================================= */}

            <FormControl
              fullWidth
              required
              disabled={
                loading ||
                !formData.organization ||
                !formData.branch ||
                filteredCourses.length ===
                  0
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,

                  '&.Mui-focused':
                    {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              >
                Course
              </InputLabel>

              <Select
                name="course"
                value={
                  formData.course ||
                  ''
                }
                label="Course"
                onChange={
                  handleChange
                }
                sx={
                  fieldSx
                }
              >
                {filteredCourses.map(
                  (
                    course
                  ) => (
                    <MenuItem
                      key={
                        course.id
                      }
                      value={
                        course.id
                      }
                    >
                      {course.course_name ||
                        course.name ||
                        `Course ${course.id}`}
                    </MenuItem>
                  )
                )}
              </Select>

              {!formData.branch && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,

                    color:
                      theme.palette
                        .warning
                        .main,
                  }}
                >
                  Select a branch first.
                </Typography>
              )}

              {formData.branch &&
                filteredCourses.length ===
                  0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,

                      color:
                        theme.palette
                          .warning
                          .main,
                    }}
                  >
                    No courses found
                    for the selected
                    organization and
                    branch.
                  </Typography>
                )}
            </FormControl>
          </Box>

          {/* =================================================
              TRAINER + TIME
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              gap: 2,

              mb: 3,
            }}
          >
            {/* =================================================
                TRAINER
            ================================================= */}

            <FormControl
              fullWidth
              required
              disabled={
                loading ||
                loadingTrainers ||
                !formData.course ||
                trainers.length ===
                  0
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,

                  '&.Mui-focused':
                    {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              >
                {loadingTrainers
                  ? 'Loading Trainers...'
                  : 'Trainer'}
              </InputLabel>

              <Select
                name="trainer"
                value={
                  formData.trainer ||
                  ''
                }
                label={
                  loadingTrainers
                    ? 'Loading Trainers...'
                    : 'Trainer'
                }
                onChange={
                  handleChange
                }
                sx={
                  fieldSx
                }
              >
                {trainers.map(
                  (
                    trainer
                  ) => (
                    <MenuItem
                      key={
                        trainer.id
                      }
                      value={
                        trainer.id
                      }
                    >
                      {getEmployeeName(
                        trainer
                      )}

                      {getEmployeeDesignation(
                        trainer
                      )
                        ? ` (${getEmployeeDesignation(
                            trainer
                          )})`
                        : ''}
                    </MenuItem>
                  )
                )}
              </Select>

              {!formData.course && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,

                    color:
                      theme.palette
                        .warning
                        .main,
                  }}
                >
                  Select a course
                  first.
                </Typography>
              )}

              {formData.course &&
                loadingTrainers && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,

                      color:
                        theme.palette
                          .info
                          .main,
                    }}
                  >
                    Loading trainers
                    for selected
                    branch...
                  </Typography>
                )}

              {formData.course &&
                formData.branch &&
                !loadingTrainers &&
                trainers.length ===
                  0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,

                      color:
                        theme.palette
                          .error
                          .main,
                    }}
                  >
                    No trainers found
                    for selected
                    organization and
                    branch.
                  </Typography>
                )}
            </FormControl>

            {/* =================================================
                BATCH TIME
            ================================================= */}

            <FormControl
              fullWidth
              required
              disabled={
                loading
              }
            >
              <TextField
                label="Batch Time"
                variant="outlined"
                fullWidth
                name="batch_time"
                value={
                  formData.batch_time
                }
                onClick={
                  handleOpenTimePicker
                }
                required
                disabled={
                  loading
                }
                InputProps={{
                  readOnly:
                    true,

                  endAdornment:
                    (
                      <IconButton
                        onClick={
                          handleOpenTimePicker
                        }
                        disabled={
                          loading
                        }
                        edge="end"
                      >
                        <AccessTimeIcon />
                      </IconButton>
                    ),
                }}
                sx={{
                  ...fieldSx,

                  cursor:
                    'pointer',
                }}
              />
            </FormControl>
          </Box>

          {/* =================================================
              DATES
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              gap: 2,

              mb: 3,
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              <TextField
                label="Start Date"
                type="date"
                variant="outlined"
                fullWidth
                name="start_date"
                value={
                  formData.start_date
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink:
                    true,
                }}
                required
                disabled={
                  loading
                }
                sx={{
                  ...fieldSx,

                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator':
                    {
                      filter:
                        theme.palette
                          .mode ===
                        'dark'
                          ? 'invert(1)'
                          : 'none',
                    },
                }}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
              }}
            >
              <TextField
                label="Completion Date"
                type="date"
                variant="outlined"
                fullWidth
                name="completion_date"
                value={
                  formData.completion_date
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink:
                    true,
                }}
                required
                disabled={
                  loading
                }
                sx={{
                  ...fieldSx,

                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator':
                    {
                      filter:
                        theme.palette
                          .mode ===
                        'dark'
                          ? 'invert(1)'
                          : 'none',
                    },
                }}
              />
            </Box>
          </Box>

          {/* =================================================
              DURATION INFO
          ================================================= */}

          <Box
            sx={{
              mb: 3,

              px: 1,
            }}
          >
            {selectedCourseDuration && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    theme.palette
                      .info
                      .main,

                  display:
                    'block',

                  mb: 0.5,
                }}
              >
                ℹ️ Course Duration:{' '}
                {
                  selectedCourseDuration
                }
                . Completion date
                is automatically
                calculated from
                the course
                duration.
              </Typography>
            )}

            {formData.start_date &&
              formData.completion_date &&
              selectedCourseDuration && (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette
                        .warning
                        .main,

                    display:
                      'block',
                  }}
                >
                  ⚠️ Changing the
                  start date will
                  automatically
                  adjust the
                  completion date.
                </Typography>
              )}
          </Box>

          {/* =================================================
              SIZE + STATUS
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              gap: 2,

              mb: 3,
            }}
          >
            <TextField
              label="Batch Size"
              variant="outlined"
              fullWidth
              name="batch_size"
              type="number"
              value={
                formData.batch_size
              }
              onChange={
                handleChange
              }
              required
              disabled={
                loading
              }
              inputProps={{
                min: 1,
              }}
              sx={
                fieldSx
              }
            />

            <FormControl
              fullWidth
              required
              disabled={
                loading
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,

                  '&.Mui-focused':
                    {
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              >
                Batch Status
              </InputLabel>

              <Select
                name="batch_status"
                value={
                  formData.batch_status
                }
                label="Batch Status"
                onChange={
                  handleChange
                }
                sx={
                  fieldSx
                }
              >
                {batchStatuses.map(
                  (
                    status
                  ) => (
                    <MenuItem
                      key={
                        status.id
                      }
                      value={
                        status.status_name
                      }
                    >
                      {
                        status.status_name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* =================================================
              REMARK
          ================================================= */}

          <Box
            sx={{
              mb: 3,
            }}
          >
            <TextField
              label="Remark"
              variant="outlined"
              fullWidth
              name="remark"
              value={
                formData.remark
              }
              onChange={
                handleChange
              }
              disabled={
                loading
              }
              multiline
              rows={3}
              sx={
                fieldSx
              }
            />
          </Box>

          {/* =================================================
              ACTION BUTTONS
          ================================================= */}

          <Box
            sx={{
              display:
                'flex',

              justifyContent:
                'flex-end',

              gap: 2,
            }}
          >
            <Tooltip
  title={
    !formData.batch_name
      ? 'Enter Batch Name'
      : !formData.course
      ? 'Select Course'
      : !formData.trainer
      ? 'Select Trainer'
      : !formData.start_date
      ? 'Select Start Date'
      : !formData.completion_date
      ? 'Select Completion Date'
      : !formData.batch_time
      ? 'Select Batch Time'
      : !formData.batch_size
      ? 'Enter Batch Size'
      : !formData.batch_status
      ? 'Select Batch Status'
      : !formData.organization
      ? 'Organization is missing'
      // Remove branch validation (handled globally)
      // : !formData.branch
      // ? 'Select Branch'
      : ''
  }
  arrow
>
              <span>
               <Button
  variant="contained"
  type="submit"
  disabled={
    loading ||
    loadingTrainers ||
    !formData.batch_name ||
    !formData.course ||
    !formData.trainer ||
    !formData.start_date ||
    !formData.completion_date ||
    !formData.batch_time ||
    !formData.batch_size ||
    !formData.batch_status ||
    !formData.organization
    // Remove branch validation (handled globally)
    // || !formData.branch
  }
  startIcon={
    loading && (
      <CircularProgress size={20} color="inherit" />
    )
  }
>
  {loading ? 'SAVING...' : 'SAVE'}
</Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="error"
              onClick={
                handleReset
              }
              disabled={
                loading
              }
            >
              RESET
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={
                handleCancel
              }
              disabled={
                loading
              }
            >
              BACK
            </Button>
          </Box>
        </form>

        {/* =================================================
            TIME PICKER
        ================================================= */}

        <Dialog
          open={
            openTimePicker
          }
          onClose={
            handleCloseTimePicker
          }
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              pb: 1,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
              >
                Select Batch Time
              </Typography>

              <IconButton
                onClick={
                  handleCloseTimePicker
                }
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={{
              pt: 1,
            }}
          >
            <Box
              sx={{
                mt: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                color="primary"
              >
                Start Time
              </Typography>

              <Grid
                container
                spacing={1}
                alignItems="center"
              >
                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Hour
                    </InputLabel>

                    <Select
                      value={selectedStartTime.split(
                        ':'
                      )[0]}
                      onChange={(
                        e
                      ) => {
                        const hour =
                          e.target
                            .value;

                        const minute =
                          selectedStartTime.split(
                            ':'
                          )[1];

                        setSelectedStartTime(
                          `${hour}:${minute}`
                        );
                      }}
                      size="small"
                    >
                      {hours.map(
                        (
                          hour
                        ) => (
                          <MenuItem
                            key={`start-hour-${hour}`}
                            value={
                              hour
                            }
                          >
                            {hour}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Minute
                    </InputLabel>

                    <Select
                      value={selectedStartTime.split(
                        ':'
                      )[1]}
                      onChange={(
                        e
                      ) => {
                        const minute =
                          e.target
                            .value;

                        const hour =
                          selectedStartTime.split(
                            ':'
                          )[0];

                        setSelectedStartTime(
                          `${hour}:${minute}`
                        );
                      }}
                      size="small"
                    >
                      {minutes.map(
                        (
                          minute
                        ) => (
                          <MenuItem
                            key={`start-minute-${minute}`}
                            value={
                              minute
                            }
                          >
                            {minute}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <Select
                      value={
                        selectedAmPmStart
                      }
                      onChange={(
                        e
                      ) =>
                        setSelectedAmPmStart(
                          e.target
                            .value
                        )
                      }
                      size="small"
                    >
                      {amPmOptions.map(
                        (
                          ampm
                        ) => (
                          <MenuItem
                            key={`start-${ampm}`}
                            value={
                              ampm
                            }
                          >
                            {ampm}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography
                variant="subtitle2"
                gutterBottom
                color="primary"
                sx={{
                  mt: 2,
                }}
              >
                End Time
              </Typography>

              <Grid
                container
                spacing={1}
                alignItems="center"
              >
                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Hour
                    </InputLabel>

                    <Select
                      value={selectedEndTime.split(
                        ':'
                      )[0]}
                      onChange={(
                        e
                      ) => {
                        const hour =
                          e.target
                            .value;

                        const minute =
                          selectedEndTime.split(
                            ':'
                          )[1];

                        setSelectedEndTime(
                          `${hour}:${minute}`
                        );
                      }}
                      size="small"
                    >
                      {hours.map(
                        (
                          hour
                        ) => (
                          <MenuItem
                            key={`end-hour-${hour}`}
                            value={
                              hour
                            }
                          >
                            {hour}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Minute
                    </InputLabel>

                    <Select
                      value={selectedEndTime.split(
                        ':'
                      )[1]}
                      onChange={(
                        e
                      ) => {
                        const minute =
                          e.target
                            .value;

                        const hour =
                          selectedEndTime.split(
                            ':'
                          )[0];

                        setSelectedEndTime(
                          `${hour}:${minute}`
                        );
                      }}
                      size="small"
                    >
                      {minutes.map(
                        (
                          minute
                        ) => (
                          <MenuItem
                            key={`end-minute-${minute}`}
                            value={
                              minute
                            }
                          >
                            {minute}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={4}
                >
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <Select
                      value={
                        selectedAmPmEnd
                      }
                      onChange={(
                        e
                      ) =>
                        setSelectedAmPmEnd(
                          e.target
                            .value
                        )
                      }
                      size="small"
                    >
                      {amPmOptions.map(
                        (
                          ampm
                        ) => (
                          <MenuItem
                            key={`end-${ampm}`}
                            value={
                              ampm
                            }
                          >
                            {ampm}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 2,

                  p: 1.5,

                  bgcolor:
                    'primary.light',

                  borderRadius: 1,

                  textAlign:
                    'center',
                }}
              >
                <Typography
                  variant="body2"
                  color="primary.contrastText"
                >
                  {
                    selectedStartTime
                  }{' '}
                  {
                    selectedAmPmStart
                  }{' '}
                  -{' '}
                  {
                    selectedEndTime
                  }{' '}
                  {
                    selectedAmPmEnd
                  }
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,

              pb: 2,
            }}
          >
            <Button
              onClick={
                handleCloseTimePicker
              }
              color="secondary"
              size="small"
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              onClick={
                handleApplyTime
              }
              variant="contained"
              color="primary"
              size="small"
            >
              Apply Time
            </Button>
          </DialogActions>
        </Dialog>

        {/* =================================================
            SUCCESS MODAL
        ================================================= */}

        <Modal
          open={
            showSuccessModal
          }
          onClose={() =>
            setShowSuccessModal(
              false
            )
          }
          BackdropProps={{
            style: {
              backgroundColor:
                'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Box
            sx={{
              position:
                'absolute',

              top: '50%',

              left: '50%',

              transform:
                'translate(-50%, -50%)',

              width: 400,

              bgcolor:
                theme.palette
                  .background
                  .paper,

              borderRadius: 2,

              boxShadow: 24,

              overflow:
                'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor:
                  theme.palette
                    .success
                    .main,

                padding:
                  '12px 16px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color:
                    theme.palette
                      .success
                      .contrastText,
                }}
              >
                Batch Added
                Successfully ✅
              </Typography>
            </Box>

            <Box
              sx={{
                padding: '20px',
              }}
            >
              <Typography
                sx={{
                  mb: 2,
                }}
              >
                Batch "
                {
                  formData.batch_name
                }
                " has been
                created
                successfully.
              </Typography>

              <Box
                sx={{
                  display:
                    'flex',

                  justifyContent:
                    'flex-end',

                  mt: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(
                      false
                    );

                    navigate(
                      '/batch'
                    );
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical:
            'top',

          horizontal:
            'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width:
              '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddBatch;